import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  exportAttendanceToExcel,
  exportAttendanceSummaryToExcel,
  exportAttendanceRangeToExcel,
  getISOWeek,
  parseISODateLocal,
} from '@/lib/exports';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  RefreshCw, Clock, CheckCircle2, XCircle, AlertTriangle, Eye,
  Send, Link2, Search, ShieldCheck, Hourglass, History, Edit3, Trash2, Download
} from 'lucide-react';

const todayISO = () => new Date().toISOString().split('T')[0];

// A record is "incomplete" (needs correction) when:
//  - It's a PAST date (before today), AND at least one of:
//    a. The staff clocked SOMETHING but is missing either the in or the out.
//    b. The record has NO resolved branch (device wasn't mapped or the
//       shift was on a floating device) - this blocks tip apportionment.
const isIncompleteRecord = (r) => {
  if (!r || !r.date) return false;
  if (r.date >= todayISO()) return false;
  const hasIn = !!r.in_time;
  const hasOut = !!r.out_time;
  const hasBreak = Array.isArray(r.breaks) && r.breaks.some(b => b && (b.in || b.out));
  const anyStamp = hasIn || hasOut || hasBreak;
  if (!anyStamp) return false;
  if (r.leave_type) return false;
  const missingPunch = (!hasIn && (hasOut || hasBreak)) || (!hasOut && (hasIn || hasBreak));
  const missingBranch = !((r.branch || '').toString().trim());
  return missingPunch || missingBranch;
};
const incompleteReasonFor = (r) => {
  if (!r) return null;
  if (!r.in_time) return 'Missing clock in';
  if (!r.out_time) return 'Missing clock out';
  if (!((r.branch || '').toString().trim())) return 'Missing branch';
  return null;
};
const daysAgoISO = (n) => new Date(Date.now() - n * 86400_000).toISOString().split('T')[0];

// Monday-to-Sunday week (matches the rest of the scheduling UI)
const currentWeekRange = () => {
  const d = new Date();
  // 0 = Sun, 1 = Mon … 6 = Sat. Walk back to Monday.
  const dow = d.getDay();
  const daysFromMon = dow === 0 ? 6 : dow - 1;
  const mon = new Date(d);
  mon.setDate(d.getDate() - daysFromMon);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const iso = (x) => x.toISOString().split('T')[0];
  return { from: iso(mon), to: iso(sun) };
};

const fmtTime = (t) => t || '-';
const fmtNum = (n) => (n == null ? '-' : Number(n).toFixed(2));
const fmtDateLocal = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

export function AdminAttendanceTab({ ctx }) {
  const { users = [], branches = [] } = ctx || {};
  const [tab, setTab] = useState('records');

  const [dateFrom, setDateFrom] = useState(daysAgoISO(7));
  const [dateTo, setDateTo] = useState(todayISO());
  const [branchFilter, setBranchFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [signoffFilter, setSignoffFilter] = useState('any');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncDays, setSyncDays] = useState(7);

  const [mappingOpen, setMappingOpen] = useState(false);
  const [timetecUsers, setTimetecUsers] = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingSearch, setMappingSearch] = useState('');

  const [editRequests, setEditRequests] = useState([]);
  const [editRequestsLoading, setEditRequestsLoading] = useState(false);
  const [editRequestStatus, setEditRequestStatus] = useState('pending');
  const [erRoleFilter, setErRoleFilter] = useState('any');
  const [erDateFrom, setErDateFrom] = useState('');
  const [erDateTo, setErDateTo] = useState('');
  const [erStaffFilter, setErStaffFilter] = useState('all');
  const [erSearch, setErSearch] = useState('');
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [decisionNote, setDecisionNote] = useState('');
  // Fields the admin can edit on approval and push straight to the record.
  const [decisionEdits, setDecisionEdits] = useState({
    in_time: '', out_time: '', breaks: [], remark: '',
  });

  // Direct admin edit/delete
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Excel/CSV summary export
  const [downloadingSummary, setDownloadingSummary] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportReportType, setExportReportType] = useState('detailed');
  const [exportRangeMode, setExportRangeMode] = useState('current_week');
  const [exportCustomFrom, setExportCustomFrom] = useState(daysAgoISO(7));
  const [exportCustomTo, setExportCustomTo] = useState(todayISO());
  const [exportEmployeeFilter, setExportEmployeeFilter] = useState('all');
  const [exportBranchFilter, setExportBranchFilter] = useState('all');
  // Signed-off only (default) vs include unsigned records too
  const [exportHoursScope, setExportHoursScope] = useState('signed_only');

  // Recompute hours (backfill)
  const [recomputingHours, setRecomputingHours] = useState(false);

  // Manual attendance record
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualForm, setManualForm] = useState({
    user_id: '',
    date: todayISO(),
    in_time: '08:00',
    out_time: '17:00',
    break1_out: '12:00',
    break1_in: '12:30',
    break2_out: '',
    break2_in: '',
    branch: '',
    leave_hours: '',
    leave_type: '',
    remark: '',
  });

  // Bulk select + admin-override approve
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  // Quick-filter: only past records missing clock in or clock out
  const [incompleteOnly, setIncompleteOnly] = useState(false);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach(u => { m[u.id] = u; });
    return m;
  }, [users]);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/timetec/sync/status`);
      setSyncStatus(r.data);
    } catch (e) {
      // silent
    }
  }, []);

  // When ON, ask the API to also return zero-shift placeholder rows so
  // admins can see (and delete) the invisible records that block manual
  // entry with a "record already exists" error.
  const [showEmpty, setShowEmpty] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoadingRecords(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (userFilter !== 'all') params.user_id = userFilter;
      if (branchFilter !== 'all') params.branch = branchFilter;
      if (signoffFilter !== 'any') params.signoff_status = signoffFilter;
      if (showEmpty) params.include_empty = true;
      const r = await axios.get(`${API}/attendance`, { params });
      setRecords(r.data.records || []);
    } catch (e) {
      toast.error('Failed to load attendance records');
    } finally {
      setLoadingRecords(false);
    }
  }, [dateFrom, dateTo, userFilter, branchFilter, signoffFilter, showEmpty]);

  const fetchEditRequests = useCallback(async () => {
    setEditRequestsLoading(true);
    try {
      const params = {};
      if (editRequestStatus !== 'all') params.status_filter = editRequestStatus;
      const r = await axios.get(`${API}/attendance/edit-requests`, { params });
      setEditRequests(r.data.requests || []);
    } catch (e) {
      toast.error('Failed to load edit requests');
    } finally {
      setEditRequestsLoading(false);
    }
  }, [editRequestStatus]);

  useEffect(() => { fetchSyncStatus(); }, [fetchSyncStatus]);
  useEffect(() => { if (tab === 'records') fetchRecords(); }, [tab, fetchRecords]);
  useEffect(() => { if (tab === 'requests') fetchEditRequests(); }, [tab, fetchEditRequests]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/timetec/sync`, null, { params: { days: syncDays } });
      if (r.data.status === 'success') {
        toast.success(`Synced ${r.data.records_synced} records (created ${r.data.records_created}, updated ${r.data.records_updated})`);
      } else {
        toast.error(`Sync failed: ${r.data.error_message || 'unknown error'}`);
      }
      fetchSyncStatus();
      if (tab === 'records') fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const openMapping = async () => {
    setMappingOpen(true);
    setMappingLoading(true);
    try {
      const r = await axios.get(`${API}/timetec/users`);
      setTimetecUsers(r.data.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch TimeTec users');
    } finally {
      setMappingLoading(false);
    }
  };

  const updateMapping = async (appUser, timetecUserId) => {
    try {
      await axios.put(`${API}/admin/users/${appUser.id}`, {
        username: appUser.username,
        password: '',
        first_name: appUser.first_name,
        last_name: appUser.last_name,
        role: appUser.role,
        branch: appUser.branch,
        branches: appUser.branches || [],
        allowed_roles: appUser.allowed_roles || [],
        email: appUser.email,
        employee_code: appUser.employee_code,
        timetec_user_id: timetecUserId || null,
        is_active: appUser.is_active !== false,
        can_work_evenings: appUser.can_work_evenings || false,
      });
      toast.success(`${appUser.username} mapped to TimeTec ID ${timetecUserId || 'cleared'}`);
      ctx?.fetchUsers?.();
      // refresh tt users to reflect mapped_to
      openMapping();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mapping failed');
    }
  };

  const decideRequest = async (req, decision) => {
    try {
      const url = `${API}/attendance/edit-requests/${req.id}/${decision}`;
      const body = { note: decisionNote || null };
      if (decision === 'approve') {
        const snap = req.attendance_snapshot || {};
        const asStr = (v) => (v == null ? '' : String(v));
        if (decisionEdits.in_time && decisionEdits.in_time !== asStr(snap.in_time)) body.in_time = decisionEdits.in_time;
        if (decisionEdits.out_time && decisionEdits.out_time !== asStr(snap.out_time)) body.out_time = decisionEdits.out_time;
        if (decisionEdits.remark && decisionEdits.remark !== asStr(snap.remark)) body.remark = decisionEdits.remark;
        const cleanBreaks = (decisionEdits.breaks || [])
          .map(b => ({ out: (b?.out || '').trim(), in: (b?.in || '').trim() }))
          .filter(b => b.out || b.in);
        if (JSON.stringify(cleanBreaks) !== JSON.stringify(snap.breaks || [])) body.breaks = cleanBreaks;
      }
      const r = await axios.post(url, body);
      toast.success(`Edit request ${decision}d`);
      setReviewingRequest(null);
      setDecisionNote('');
      setDecisionEdits({ in_time: '', out_time: '', breaks: [], remark: '' });
      fetchEditRequests();
      if (tab === 'records') fetchRecords();
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${decision}`);
      return null;
    }
  };

  const filteredRecords = useMemo(() => {
    let base = records;
    if (sourceFilter !== 'all') {
      const want = sourceFilter === 'biometric' ? 'biometric' : 'timetec';
      base = base.filter(r => (r.source || 'timetec') === want);
    }
    if (incompleteOnly) {
      base = base.filter(r => isIncompleteRecord(r));
    }
    if (!search.trim()) return base;
    const s = search.toLowerCase();
    return base.filter(r =>
      (r.name || '').toLowerCase().includes(s) ||
      (r.employee_id || '').toLowerCase().includes(s) ||
      (r.timetec_user_id || '').toLowerCase().includes(s) ||
      (userMap[r.user_id]?.username || '').toLowerCase().includes(s)
    );
  }, [records, search, userMap, sourceFilter, incompleteOnly]);

  const incompleteCount = useMemo(
    () => records.reduce((acc, r) => acc + (isIncompleteRecord(r) ? 1 : 0), 0),
    [records]
  );

  const openEdit = (r) => {
    setEditingRecord(r);
    setEditForm({
      in_time: r.in_time || '',
      out_time: r.out_time || '',
      breaks: Array.isArray(r.breaks)
        ? r.breaks.map(b => ({ out: b?.out || '', in: b?.in || '' }))
        : [],
      work_hours: r.work_hours ?? '',
      ordinary_hours: r.ordinary_hours ?? '',
      daily_ot_hours: r.daily_ot_hours ?? r.ot_hours ?? '',
      sunday_hours: r.sunday_hours ?? '',
      public_holiday_hours: r.public_holiday_hours ?? '',
      weekly_ot_hours: r.weekly_ot_hours ?? '',
      leave_hours: r.leave_hours ?? '',
      leave_type: r.leave_type || '',
      remark: r.remark || '',
      branch: r.branch || '',
    });
  };

  const submitEdit = async () => {
    if (!editingRecord) return;
    try {
      const payload = { ...editForm };
      // Clean breaks: drop rows where both out and in are empty; keep the
      // rest even if one half is missing so the admin can flag it.
      if (Array.isArray(payload.breaks)) {
        payload.breaks = payload.breaks
          .map(b => ({ out: (b?.out || '').trim(), in: (b?.in || '').trim() }))
          .filter(b => b.out || b.in);
      }
      // Convert empty strings to null — EXCEPT branch and breaks.
      Object.keys(payload).forEach(k => {
        if (k === 'branch' || k === 'breaks') return;
        if (payload[k] === '') payload[k] = null;
      });
      ['work_hours', 'ordinary_hours', 'daily_ot_hours', 'sunday_hours',
       'public_holiday_hours', 'weekly_ot_hours', 'leave_hours'].forEach(k => {
        if (payload[k] !== null && payload[k] !== undefined) payload[k] = parseFloat(payload[k]);
      });
      await axios.put(`${API}/attendance/${editingRecord.id}`, payload);
      toast.success('Attendance updated');
      setEditingRecord(null);
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteRecord = async (r) => {
    if (!window.confirm(`Delete attendance record for ${r.name} on ${r.date}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/attendance/${r.id}`);
      toast.success('Record deleted');
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const openExportDialog = () => {
    // Default to currently-loaded range if it differs from "current week"
    const wk = currentWeekRange();
    setExportReportType('detailed');
    setExportRangeMode('current_week');
    setExportCustomFrom(dateFrom || wk.from);
    setExportCustomTo(dateTo || wk.to);
    setExportEmployeeFilter('all');
    setExportBranchFilter('all');
    setExportHoursScope('signed_only');
    setExportDialogOpen(true);
  };

  const runExport = async () => {
    const range = exportRangeMode === 'current_week'
      ? currentWeekRange()
      : { from: exportCustomFrom, to: exportCustomTo };
    if (exportRangeMode === 'custom' && (!range.from || !range.to)) {
      toast.error('Please pick both a From and To date.');
      return;
    }
    if (range.from > range.to) {
      toast.error('From date must be before To date.');
      return;
    }

    // Fetch attendance for the exact export range so we don't rely on the
    // currently-loaded table filter.
    setDownloadingSummary(true);
    try {
      // Fetch attendance rules once so we can compute night-shift allowance
      // in-browser without needing every record to have been recomputed.
      let rules = {};
      try {
        const rr = await axios.get(`${API}/attendance/rules`);
        rules = rr.data || {};
      } catch {
        // fail-soft — night shift column will just show R0
      }

      let recs;
      try {
        const r = await axios.get(`${API}/attendance`, {
          params: { date_from: range.from, date_to: range.to },
        });
        recs = Array.isArray(r.data) ? r.data : (r.data?.records || []);
      } catch {
        toast.error('Failed to load attendance for that range.');
        return;
      }

      // Attach employee_id fallback: many records key on user_id, not
      // employee_id — mirror it so the export library's Map keys line up.
      recs = recs.map(r => ({
        ...r,
        employee_id: r.employee_id || r.user_id || '',
        employee_name: r.name,
        clock_in: r.in_time,
        clock_out: r.out_time,
        total_hours: r.work_hours,
      }));

      // Signed-off scope: default 'signed_only' keeps payroll numbers
      // auditable (matches the Gratuities engine). 'all_hours' includes
      // unsigned records for a raw operational view.
      const totalBeforeFilter = recs.length;
      if (exportReportType !== 'detailed') {
        // summary totals are fetched server-side below
      } else if (exportHoursScope === 'signed_only') {
        recs = recs.filter(r =>
          r.admin_override === true ||
          (r.employee_signoff && r.manager_signoff)
        );
        const droppedUnsigned = totalBeforeFilter - recs.length;
        if (droppedUnsigned > 0) {
          toast.info(`${droppedUnsigned} unsigned record(s) excluded from export — only fully signed-off records are included.`);
        }
      } else {
        const unsignedIncluded = recs.filter(r =>
          r.admin_override !== true && !(r.employee_signoff && r.manager_signoff)
        ).length;
        if (unsignedIncluded > 0) {
          toast.info(`${unsignedIncluded} unsigned record(s) included in export (All hours mode).`);
        }
      }

      // Optional in-dialog filters
      if (exportEmployeeFilter !== 'all') {
        recs = recs.filter(r =>
          r.user_id === exportEmployeeFilter || r.employee_id === exportEmployeeFilter
        );
      }
      if (exportBranchFilter !== 'all') {
        recs = recs.filter(r => (r.branch || '') === exportBranchFilter);
      }

      if (exportReportType !== 'detailed') {
        // Totals come from the shared server calculation (same as staff payroll screen).
        let periodRows;
        try {
          const pr = await axios.get(`${API}/attendance/period-totals`, {
            params: {
              date_from: range.from,
              date_to: range.to,
              ...(exportEmployeeFilter !== 'all' ? { user_id: exportEmployeeFilter } : {}),
              ...(exportBranchFilter !== 'all' ? { branch: exportBranchFilter } : {}),
            },
          });
          periodRows = pr.data?.rows || [];
        } catch {
          toast.error('Failed to load period totals.');
          return;
        }
        // Gratuities + stokvel in parallel (both fail-soft).
        const gratMap = {};
        let stokvelMap = {};
        const [gr, sk] = await Promise.allSettled([
          axios.get(`${API}/gratuities/report`, { params: { start_date: range.from, end_date: range.to } }),
          axios.get(`${API}/stokvel/admin/payroll-items`, { params: { date_from: range.from, date_to: range.to } }),
        ]);
        if (gr.status === 'fulfilled') {
          (gr.value.data?.totals_by_employee || []).forEach(t => { gratMap[t.employee_id] = Number(t.total_amount) || 0; });
          if (gr.value.data?.errors?.length) toast.warning('Gratuities data partially unavailable — defaulting to R0.00 where missing.');
        } else {
          toast.warning('Cash Recon unreachable — Gratuities column defaulted to R0.00.');
        }
        if (sk.status === 'fulfilled') {
          stokvelMap = sk.value.data?.items || {};
          const have = new Set(periodRows.map(r => r.user_id));
          Object.entries(stokvelMap).forEach(([uid, it]) => {
            if (have.has(uid)) return;
            if (exportEmployeeFilter !== 'all' && exportEmployeeFilter !== uid) return;
            if (exportBranchFilter !== 'all') return;
            periodRows.push({ user_id: uid, name: it.name, employee_code: it.employee_code || '' });
          });
        } else {
          toast.warning('Could not load stokvel deductions — those columns will be empty.');
        }
        const { filename, rowCount } = exportAttendanceSummaryToExcel(
          periodRows, range.from, range.to, gratMap, rules, stokvelMap,
          exportReportType === 'simplepay' ? 'simplepay' : 'xlsx',
        );
        toast.success(`${exportReportType === 'simplepay' ? 'SimplePay file' : 'Summary'} exported · ${rowCount} employee(s) · ${filename}`);
      } else {
        // Detailed — if the range fits in a single ISO week, use the
        // week-number filename; otherwise fall through to the range filename.
        const from = parseISODateLocal(range.from);
        const to = parseISODateLocal(range.to);
        const spanDays = Math.round((to - from) / 86400000) + 1;
        if (spanDays <= 7) {
          const wkNum = getISOWeek(from);
          const yr = from.getFullYear();
          const { filename, rowCount } = exportAttendanceToExcel(recs, wkNum, yr, rules);
          toast.success(`Detailed Week ${wkNum} exported · ${rowCount} record(s) · ${filename}`);
        } else {
          const { filename, rowCount } = exportAttendanceRangeToExcel(
            recs, range.from, range.to, 'detailed', rules,
          );
          toast.success(`Detailed report exported · ${rowCount} record(s) · ${filename}`);
        }
      }
      setExportDialogOpen(false);
    } finally {
      setDownloadingSummary(false);
    }
  };

  const recomputeHours = async () => {
    if (!window.confirm(
      'Walk every attendance record and recompute total_hours from clock in/out and breaks?\n\n' +
      'Only records that change will be updated. This is idempotent.'
    )) return;
    setRecomputingHours(true);
    try {
      const r = await axios.post(`${API}/attendance/recompute-hours`);
      toast.success(
        `Recompute done · inspected ${r.data.inspected} · updated ${r.data.updated} · unchanged ${r.data.unchanged}`
      );
      if (tab === 'records') fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recompute failed');
    } finally {
      setRecomputingHours(false);
    }
  };

  const openManual = () => {
    setManualForm({
      user_id: '',
      date: todayISO(),
      in_time: '08:00',
      out_time: '17:00',
      break1_out: '12:00',
      break1_in: '12:30',
      break2_out: '',
      break2_in: '',
      branch: '',
      leave_hours: '',
      leave_type: '',
      remark: '',
    });
    setManualOpen(true);
  };

  const submitManual = async () => {
    if (!manualForm.user_id) { toast.error('Pick a staff member.'); return; }
    if (!manualForm.date) { toast.error('Pick a date.'); return; }
    if (!manualForm.in_time || !manualForm.out_time) {
      toast.error('Clock In and Clock Out are required.'); return;
    }
    setManualSaving(true);
    try {
      const payload = {
        user_id: manualForm.user_id,
        date: manualForm.date,
        in_time: manualForm.in_time,
        out_time: manualForm.out_time,
        break1_out: manualForm.break1_out || null,
        break1_in: manualForm.break1_in || null,
        break2_out: manualForm.break2_out || null,
        break2_in: manualForm.break2_in || null,
        branch: manualForm.branch || null,
        leave_hours: manualForm.leave_hours ? Number(manualForm.leave_hours) : 0,
        leave_type: manualForm.leave_type || null,
        remark: manualForm.remark || null,
      };
      const r = await axios.post(`${API}/attendance/manual`, payload);
      const rec = r.data?.record;
      toast.success(
        `Manual record created · ${rec?.work_hours ?? 0}h for ${rec?.name || ''} on ${rec?.date}`
      );
      setManualOpen(false);
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create record');
    } finally {
      setManualSaving(false);
    }
  };

  // Bulk admin-override approve
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllVisible = (checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        filteredRecords.forEach(r => next.add(r.id));
      } else {
        filteredRecords.forEach(r => next.delete(r.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const filteredEditRequests = useMemo(() => {
    return editRequests.filter(r => {
      if (erRoleFilter !== 'any' && r.submitted_by_role !== erRoleFilter) return false;
      if (erDateFrom && r.subject_date < erDateFrom) return false;
      if (erDateTo && r.subject_date > erDateTo) return false;
      if (erStaffFilter !== 'all' && r.subject_user_id !== erStaffFilter) return false;
      if (erSearch) {
        const q = erSearch.toLowerCase();
        const hay = `${r.subject_name || ''} ${r.submitted_by_username || ''} ${r.reason || ''} ${r.requested_changes || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [editRequests, erRoleFilter, erDateFrom, erDateTo, erStaffFilter, erSearch]);

  const clearEditRequestFilters = () => {
    setEditRequestStatus('pending');
    setErRoleFilter('any');
    setErDateFrom('');
    setErDateTo('');
    setErStaffFilter('all');
    setErSearch('');
  };

  const subjectStaffOptions = useMemo(() => {
    const seen = new Map();
    editRequests.forEach(r => {
      if (r.subject_user_id && !seen.has(r.subject_user_id)) {
        const rawName = (r.subject_name || '').trim();
        const isPlaceholder = !rawName || /^none\s+none$/i.test(rawName);
        const label = (isPlaceholder ? '' : rawName) ||
          userMap[r.subject_user_id]?.username ||
          r.subject_user_id;
        seen.set(r.subject_user_id, label);
      }
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [editRequests, userMap]);


  const visibleSelectedCount = useMemo(
    () => filteredRecords.filter(r => selectedIds.has(r.id)).length,
    [filteredRecords, selectedIds]
  );
  const allVisibleSelected = filteredRecords.length > 0 &&
    visibleSelectedCount === filteredRecords.length;

  const unsignedRecords = useMemo(
    () => filteredRecords.filter(r =>
      !(r.employee_signoff && r.manager_signoff)
    ),
    [filteredRecords]
  );

  const approveAllUnsigned = async () => {
    const ids = unsignedRecords.map(r => r.id);
    if (ids.length === 0) {
      toast.info('No unsigned records in this period — everything is already approved.');
      return;
    }
    if (!window.confirm(
      `Approve ALL ${ids.length} unsigned attendance record(s) for ${dateFrom} → ${dateTo}?\n\n` +
      `Both employee and manager sign-offs will be stamped as you, and admin_override will be set on each. Records that already have both sign-offs are skipped.`
    )) return;
    setBulkApproving(true);
    try {
      const r = await axios.post(
        `${API}/attendance/signoff/admin-override/bulk`,
        { record_ids: ids }
      );
      toast.success(r.data?.message || `${ids.length} record(s) approved`);
      clearSelection();
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setBulkApproving(false);
    }
  };

  const bulkApproveSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error('No records selected');
      return;
    }
    if (!window.confirm(
      `Approve ${ids.length} attendance record(s) via admin override?\n\n` +
      `Both employee and manager sign-offs will be stamped as you, and admin_override will be set so future biometric punches won't overwrite these records.`
    )) return;
    setBulkApproving(true);
    try {
      const r = await axios.post(
        `${API}/attendance/signoff/admin-override/bulk`,
        { record_ids: ids }
      );
      toast.success(r.data?.message || `${ids.length} record(s) approved`);
      clearSelection();
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setBulkApproving(false);
    }
  };

  const filteredTimetecUsers = useMemo(() => {
    if (!mappingSearch.trim()) return timetecUsers;
    const s = mappingSearch.toLowerCase();
    return timetecUsers.filter(u =>
      (u.name || '').toLowerCase().includes(s) ||
      (u.email || '').toLowerCase().includes(s) ||
      (u.employee_id || '').toLowerCase().includes(s) ||
      (u.timetec_user_id || '').toLowerCase().includes(s)
    );
  }, [timetecUsers, mappingSearch]);

  const latest = syncStatus?.latest;
  const lastSyncBadge = latest
    ? (latest.status === 'success'
        ? <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full text-xs"><CheckCircle2 className="w-3 h-3 inline mr-1" />Last sync OK · {fmtDateLocal(latest.completed_at || latest.started_at)}</span>
        : <span className="text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full text-xs"><AlertTriangle className="w-3 h-3 inline mr-1" />Last sync failed</span>)
    : <span className="text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full text-xs">No sync yet</span>;

  return (
    <div className="space-y-4" data-testid="admin-attendance-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-xl border border-stone-200">
        <div>
          <div className="text-lg font-semibold text-stone-800 flex items-center gap-2">
            <Clock className="w-5 h-5" /> Attendance
          </div>
          <div className="text-xs text-stone-500 mt-1 flex items-center gap-2 flex-wrap">
            <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full text-xs">Live · Biometric (ADMS)</span>
            <span className="text-stone-400">|</span>
            <span>TimeTec cloud sync retired - biometric devices push directly to the app.</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openMapping} data-testid="open-mapping-button">
            <Link2 className="w-4 h-4 mr-2" /> Map TimeTec IDs
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="records" data-testid="tab-attendance-records">Attendance</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-attendance-requests">
            Edit Requests
            {syncStatus && editRequests.filter(r => r.status === 'pending').length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center bg-rose-500 text-white text-xs rounded-full w-5 h-5">
                {editRequests.filter(r => r.status === 'pending').length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="records">
          {/* Filters */}
          <div className="bg-white p-4 rounded-xl border border-stone-200 mb-3 grid grid-cols-1 md:grid-cols-7 gap-3">
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="attendance-date-from" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="attendance-date-to" />
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger data-testid="attendance-source-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="biometric">Live · Biometric</SelectItem>
                  <SelectItem value="timetec">History · TimeTec</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Branch</Label>
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger data-testid="attendance-branch-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {branches.map(b => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">User</Label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger data-testid="attendance-user-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sign-off</Label>
              <Select value={signoffFilter} onValueChange={setSignoffFilter}>
                <SelectTrigger data-testid="attendance-signoff-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="none">Not signed</SelectItem>
                  <SelectItem value="employee_only">Employee only</SelectItem>
                  <SelectItem value="manager_only">Manager (any)</SelectItem>
                  <SelectItem value="both">Both signed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-stone-400" />
                <Input value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-8" placeholder="Name, code…" data-testid="attendance-search" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setIncompleteOnly(v => !v)}
                data-testid="incomplete-toggle-admin"
                className={`inline-flex items-center gap-2 rounded-full text-xs font-medium px-3 py-1.5 border transition ${
                  incompleteOnly
                    ? 'bg-red-600 text-white border-red-600 shadow-sm hover:bg-red-700'
                    : incompleteCount > 0
                      ? 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100'
                      : 'bg-stone-50 text-stone-500 border-stone-200 hover:bg-stone-100'
                }`}
                title="Past records missing clock in or clock out (with other stamps present)"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                <span data-testid="incomplete-count-admin">{incompleteCount}</span>
                <span>incomplete</span>
                {incompleteOnly && <XCircle className="w-3 h-3 ml-1" />}
              </button>
              <button
                type="button"
                onClick={() => setShowEmpty(v => !v)}
                data-testid="show-empty-toggle-admin"
                className={`inline-flex items-center gap-2 rounded-full text-xs font-medium px-3 py-1.5 border transition ${
                  showEmpty
                    ? 'bg-stone-800 text-white border-stone-800 shadow-sm hover:bg-stone-900'
                    : 'bg-stone-50 text-stone-500 border-stone-200 hover:bg-stone-100'
                }`}
                title="Include zero-shift placeholder rows (invisible by default) so you can delete or fix them"
              >
                <Eye className="w-3.5 h-3.5" />
                <span>{showEmpty ? 'Showing empty rows' : 'Show empty rows'}</span>
              </button>
              {selectedIds.size === 0 && unsignedRecords.length > 0 && (
                <Button size="sm"
                  onClick={approveAllUnsigned}
                  disabled={bulkApproving}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="approve-all-unsigned-button">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  {bulkApproving
                    ? 'Approving…'
                    : `Approve all unsigned (${unsignedRecords.length})`}
                </Button>
              )}
              {selectedIds.size > 0 && (
                <>
                  <span className="text-sm text-stone-600 bg-stone-100 rounded-full px-3 py-1"
                    data-testid="selected-count">
                    {selectedIds.size} selected
                  </span>
                  <Button size="sm"
                    onClick={bulkApproveSelected}
                    disabled={bulkApproving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="bulk-admin-approve-button">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    {bulkApproving ? 'Approving…' : `Approve ${selectedIds.size} (Admin override)`}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearSelection}
                    data-testid="clear-selection-button">
                    Clear
                  </Button>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm"
                onClick={openManual}
                data-testid="add-manual-record-button">
                <Edit3 className="w-4 h-4 mr-2" />
                Add Manual Record
              </Button>
              <Button variant="outline" size="sm"
                onClick={recomputeHours}
                disabled={recomputingHours}
                title="Walk every attendance record and recompute total_hours from clock in/out and breaks"
                data-testid="recompute-hours-button">
                <RefreshCw className={`w-4 h-4 mr-2 ${recomputingHours ? 'animate-spin' : ''}`} />
                {recomputingHours ? 'Recomputing…' : 'Recompute Hours'}
              </Button>
              <Button variant="outline" size="sm"
                onClick={openExportDialog}
                disabled={downloadingSummary}
                data-testid="download-attendance-summary-button">
                <Download className={`w-4 h-4 mr-2 ${downloadingSummary ? 'animate-pulse' : ''}`} />
                {downloadingSummary ? 'Building…' : 'Export Attendance'}
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 text-stone-700">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <Checkbox
                        checked={allVisibleSelected}
                        onCheckedChange={(v) => toggleSelectAllVisible(!!v)}
                        data-testid="select-all-checkbox"
                        aria-label="Select all visible"
                      />
                    </th>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Staff</th>
                    <th className="text-left px-3 py-2">Source</th>
                    <th className="text-left px-3 py-2">Branch</th>
                    <th className="text-left px-3 py-2">In</th>
                    <th className="text-left px-3 py-2">Out</th>
                    <th className="text-left px-3 py-2">Breaks</th>
                    <th className="text-right px-3 py-2">Hours</th>
                    <th className="text-right px-3 py-2">Ord</th>
                    <th className="text-right px-3 py-2">W.OT</th>
                    <th className="text-right px-3 py-2">Sun</th>
                    <th className="text-right px-3 py-2">PH</th>
                    <th className="text-right px-3 py-2" title="Public Holiday Extra Hours — top-up to reach the greater of (2× PH worked) or (2× normal daily hours) per BCEA §18">PH+</th>
                    <th className="text-center px-3 py-2">Emp</th>
                    <th className="text-center px-3 py-2">Mgr</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRecords && (
                    <tr><td colSpan={16} className="text-center text-stone-400 py-6">Loading…</td></tr>
                  )}
                  {!loadingRecords && filteredRecords.length === 0 && (
                    <tr><td colSpan={16} className="text-center text-stone-400 py-6">No attendance records in this range.</td></tr>
                  )}
                  {!loadingRecords && filteredRecords.map(r => {
                    const appUser = r.user_id ? userMap[r.user_id] : null;
                    const isSelected = selectedIds.has(r.id);
                    const incomplete = isIncompleteRecord(r);
                    const incompleteReason = incomplete ? incompleteReasonFor(r) : null;
                    return (
                      <tr key={r.id}
                        className={`border-t border-stone-100 ${
                          isSelected
                            ? 'bg-emerald-50'
                            : incomplete
                              ? 'bg-red-50 hover:bg-red-100'
                              : 'hover:bg-stone-50'
                        }`}
                        data-testid={incomplete ? `att-row-incomplete-${r.id}` : undefined}>
                        <td className="px-3 py-2">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(r.id)}
                            data-testid={`select-attendance-${r.id}`}
                            aria-label="Select record"
                          />
                        </td>
                        <td className="px-3 py-2 font-mono">
                          <div className="flex items-center gap-1.5">
                            {incomplete && (
                              <span
                                className="inline-flex items-center gap-0.5 rounded-full bg-red-600 text-white text-[10px] font-semibold px-1.5 py-0.5"
                                title={incompleteReason}
                              >
                                <AlertTriangle className="w-2.5 h-2.5" />
                                !
                              </span>
                            )}
                            <span>{r.date}</span>
                          </div>
                          {(r.is_public_holiday || r.is_sunday) && (
                            <div className="text-xs">
                              {r.is_public_holiday && <span className="text-rose-700">PH · {r.public_holiday_name || ''}</span>}
                              {r.is_sunday && !r.is_public_holiday && <span className="text-amber-700">Sunday</span>}
                            </div>
                          )}
                          {incomplete && (
                            <div className="text-xs text-red-700 font-medium">{incompleteReason}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-stone-800">{appUser?.username || r.name}</div>
                          <div className="text-xs text-stone-500">{r.name}{r.employee_id && ` · ${r.employee_id}`}{!r.user_id && <span className="ml-1 text-amber-600">· unmapped</span>}</div>
                        </td>
                        <td className="px-3 py-2">
                          {r.source === 'biometric'
                            ? <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full text-xs">Live · Biometric{r.admin_override && ' · ✎'}</span>
                            : <span className="text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full text-xs">History · TimeTec</span>}
                        </td>
                        <td className="px-3 py-2" data-testid={`attendance-row-branch-${r.id}`}>
                          {r.branch
                            ? <span className="text-stone-700">{r.branch}</span>
                            : <span className="text-stone-400 italic text-xs">unresolved</span>}
                        </td>
                        <td className="px-3 py-2 font-mono">{fmtTime(r.in_time)}
                          {r.punch_methods?.includes('mobile') && <span className="ml-1 px-1 rounded bg-sky-100 text-sky-800 text-[10px] font-sans font-semibold align-middle" title="Includes manual punches from the staff portal" data-testid={`mobile-punch-badge-${r.id}`}>Mobile</span>}
                        </td>
                        <td className="px-3 py-2 font-mono">{fmtTime(r.out_time)}</td>
                        <td className="px-3 py-2 font-mono text-xs" data-testid={`breaks-cell-${r.id}`}>
                          {Array.isArray(r.breaks) && r.breaks.length > 0
                            ? (
                              <div className="flex flex-col gap-0.5">
                                {r.breaks.map((b, i) => (
                                  <span key={i} className="text-stone-700 whitespace-nowrap">
                                    <span className="text-stone-400 mr-1">{i + 1}.</span>
                                    <span className="text-rose-600">{b.out || '—'}</span>
                                    <span className="text-stone-400 mx-1">→</span>
                                    <span className="text-emerald-700">{b.in || '—'}</span>
                                  </span>
                                ))}
                              </div>
                            )
                            : <span className="text-stone-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">{fmtNum(r.work_hours)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtNum(r.ordinary_hours)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtNum(r.weekly_ot_hours)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtNum(r.sunday_hours)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtNum(r.public_holiday_hours)}</td>
                        <td className="px-3 py-2 text-right font-mono" title={r.normal_dow_hours ? `Normal for this day-of-week: ${r.normal_dow_hours}h` : ''}>
                          {r.is_public_holiday && Number(r.public_holiday_extra_hours) > 0
                            ? <span className="text-rose-700 font-semibold">{fmtNum(r.public_holiday_extra_hours)}</span>
                            : <span className="text-stone-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.employee_signoff
                            ? <span title={`by ${r.employee_signoff.by_username} at ${fmtDateLocal(r.employee_signoff.at)}`}
                                className="inline-flex items-center text-emerald-700 text-xs">
                                <CheckCircle2 className="w-4 h-4" />
                              </span>
                            : <Hourglass className="w-4 h-4 text-stone-300 inline" />}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {r.manager_signoff
                            ? <span title={`by ${r.manager_signoff.by_username} at ${fmtDateLocal(r.manager_signoff.at)}`}
                                className="inline-flex items-center text-emerald-700 text-xs">
                                <ShieldCheck className="w-4 h-4" />
                              </span>
                            : <Hourglass className="w-4 h-4 text-stone-300 inline" />}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(r)}
                            data-testid={`edit-attendance-${r.id}`} title="Edit">
                            <Edit3 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-rose-600"
                            onClick={() => deleteRecord(r)}
                            data-testid={`delete-attendance-${r.id}`} title="Delete">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-2 text-xs text-stone-500 border-t border-stone-200">
              {filteredRecords.length} record(s) shown
            </div>
          </div>
        </TabsContent>

        <TabsContent value="requests">
          <div className="bg-white p-4 rounded-xl border border-stone-200 mb-3 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={editRequestStatus} onValueChange={setEditRequestStatus}>
                  <SelectTrigger className="w-40" data-testid="edit-request-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Submitted by</Label>
                <Select value={erRoleFilter} onValueChange={setErRoleFilter}>
                  <SelectTrigger className="w-36" data-testid="er-role-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any role</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Staff member</Label>
                <Select value={erStaffFilter} onValueChange={setErStaffFilter}>
                  <SelectTrigger className="w-48" data-testid="er-staff-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All staff</SelectItem>
                    {subjectStaffOptions.map(([id, label]) => (
                      <SelectItem key={id} value={id}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Record date from</Label>
                <Input type="date" value={erDateFrom} onChange={e => setErDateFrom(e.target.value)}
                  className="w-40" data-testid="er-date-from" />
              </div>
              <div>
                <Label className="text-xs">Record date to</Label>
                <Input type="date" value={erDateTo} onChange={e => setErDateTo(e.target.value)}
                  className="w-40" data-testid="er-date-to" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-2.5 text-stone-400" />
                  <Input value={erSearch} onChange={e => setErSearch(e.target.value)}
                    className="pl-8" placeholder="Staff, submitter, reason, changes…"
                    data-testid="er-search" />
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={fetchEditRequests}>
                <RefreshCw className={`w-3 h-3 mr-1 ${editRequestsLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={clearEditRequestFilters}
                className="text-stone-500" data-testid="er-clear-filters">
                Clear
              </Button>
            </div>
            <div className="text-xs text-stone-500" data-testid="er-result-count">
              <span className="font-mono">{filteredEditRequests.length}</span>
              <span> of {editRequests.length} request{editRequests.length === 1 ? '' : 's'}</span>
              {editRequests.length !== filteredEditRequests.length && (
                <span className="text-amber-700"> · filtered</span>
              )}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 text-stone-700">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Staff</th>
                    <th className="text-left px-3 py-2">Submitted by</th>
                    <th className="text-left px-3 py-2">Reason</th>
                    <th className="text-left px-3 py-2">Requested</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEditRequests.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-stone-400 py-6">No edit requests.</td></tr>
                  )}
                  {filteredEditRequests.map(r => (
                    <tr key={r.id} className="border-t border-stone-100">
                      <td className="px-3 py-2 font-mono">{r.subject_date}</td>
                      <td className="px-3 py-2">
                        {(() => {
                          const raw = (r.subject_name || '').trim();
                          const isPlaceholder = !raw || /^none\s+none$/i.test(raw);
                          return isPlaceholder
                            ? (userMap[r.subject_user_id]?.username || '-')
                            : raw;
                        })()}
                      </td>
                      <td className="px-3 py-2 text-xs">{r.submitted_by_username} <span className="text-stone-400">({r.submitted_by_role})</span></td>
                      <td className="px-3 py-2 text-xs text-stone-600 max-w-xs truncate" title={r.reason}>{r.reason}</td>
                      <td className="px-3 py-2 text-xs text-stone-600 max-w-xs truncate" title={r.requested_changes}>{r.requested_changes}</td>
                      <td className="px-3 py-2">
                        {r.status === 'pending' && <span className="text-amber-700 bg-amber-100 px-2 py-0.5 rounded text-xs">Pending</span>}
                        {r.status === 'approved' && <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded text-xs">Approved</span>}
                        {r.status === 'declined' && <span className="text-rose-700 bg-rose-100 px-2 py-0.5 rounded text-xs">Declined</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={() => {
                            const snap = r.attendance_snapshot || {};
                            setReviewingRequest(r);
                            setDecisionNote('');
                            setDecisionEdits({
                              in_time: snap.in_time || '',
                              out_time: snap.out_time || '',
                              breaks: Array.isArray(snap.breaks) ? snap.breaks.map(b => ({ out: b?.out || '', in: b?.in || '' })) : [],
                              remark: snap.remark || '',
                            });
                          }}
                            data-testid={`review-request-${r.id}`}>
                            Review
                          </Button>
                        )}
                        {r.status !== 'pending' && (
                          <Button size="sm" variant="ghost" onClick={() => setReviewingRequest(r)}>
                            <History className="w-3 h-3 mr-1" /> Details
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Mapping dialog */}
      <Dialog open={mappingOpen} onOpenChange={setMappingOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="mapping-dialog">
          <DialogHeader>
            <DialogTitle>Map TimeTec users to app users</DialogTitle>
          </DialogHeader>
          <div className="mb-3">
            <Input placeholder="Search TimeTec users by name / employee id / email"
              value={mappingSearch} onChange={e => setMappingSearch(e.target.value)} data-testid="mapping-search" />
          </div>
          {mappingLoading && <div className="text-center text-stone-400 py-6">Loading TimeTec users…</div>}
          {!mappingLoading && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-100 text-stone-700">
                  <tr>
                    <th className="text-left px-3 py-2">TimeTec User</th>
                    <th className="text-left px-3 py-2">Employee ID</th>
                    <th className="text-left px-3 py-2">Mapped to (app user)</th>
                    <th className="text-left px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTimetecUsers.map(tt => {
                    const mappedAppUser = users.find(u => String(u.timetec_user_id) === String(tt.timetec_user_id));
                    return (
                      <tr key={tt.timetec_user_id} className="border-t border-stone-100">
                        <td className="px-3 py-2">
                          <div className="font-medium">{tt.name}</div>
                          <div className="text-xs text-stone-500">{tt.email}</div>
                          <div className="text-xs text-stone-400 font-mono">TT ID: {tt.timetec_user_id}</div>
                        </td>
                        <td className="px-3 py-2 font-mono">{tt.employee_id || '-'}</td>
                        <td className="px-3 py-2">
                          {mappedAppUser
                            ? <span className="text-emerald-700 text-xs">{mappedAppUser.username}</span>
                            : <span className="text-stone-400 text-xs">— not mapped —</span>}
                        </td>
                        <td className="px-3 py-2">
                          <Select value={mappedAppUser?.id || ''}
                            onValueChange={(uid) => {
                              if (!uid) return;
                              const appUser = users.find(u => u.id === uid);
                              if (appUser) updateMapping(appUser, tt.timetec_user_id);
                            }}>
                            <SelectTrigger className="w-56" data-testid={`mapping-select-${tt.timetec_user_id}`}>
                              <SelectValue placeholder="Select app user…" />
                            </SelectTrigger>
                            <SelectContent>
                              {users.filter(u => u.is_active !== false).map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {mappedAppUser && (
                            <Button variant="ghost" size="sm" className="ml-2 text-rose-600"
                              onClick={() => updateMapping(mappedAppUser, null)}>
                              Unmap
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewingRequest} onOpenChange={(o) => { if (!o) { setReviewingRequest(null); setDecisionNote(''); } }}>
        <DialogContent className="max-w-2xl" data-testid="review-request-dialog">
          {reviewingRequest && (
            <>
              <DialogHeader>
                <DialogTitle>Attendance edit request</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2 bg-stone-50 p-3 rounded">
                  <div><span className="text-stone-500">Staff:</span> {reviewingRequest.subject_name}</div>
                  <div><span className="text-stone-500">Date:</span> {reviewingRequest.subject_date}</div>
                  <div><span className="text-stone-500">Submitted by:</span> {reviewingRequest.submitted_by_username} ({reviewingRequest.submitted_by_role})</div>
                  <div><span className="text-stone-500">Status:</span> <span className="capitalize">{reviewingRequest.status}</span></div>
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Reason</Label>
                  <div className="border border-stone-200 rounded p-2 whitespace-pre-wrap">{reviewingRequest.reason}</div>
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Requested changes</Label>
                  <div className="border border-stone-200 rounded p-2 whitespace-pre-wrap">{reviewingRequest.requested_changes}</div>
                </div>
                {reviewingRequest.attendance_snapshot && (
                  <div className="text-xs text-stone-600 bg-stone-50 p-2 rounded font-mono">
                    Snapshot at submission — In: {reviewingRequest.attendance_snapshot.in_time || '-'} ·
                    Out: {reviewingRequest.attendance_snapshot.out_time || '-'} ·
                    Hours: {fmtNum(reviewingRequest.attendance_snapshot.work_hours)}
                  </div>
                )}
                {reviewingRequest.status === 'approved' && reviewingRequest.verification_diff && (
                  <div className="bg-emerald-50 border border-emerald-200 p-3 rounded text-xs">
                    <div className="font-medium mb-1">Applied changes</div>
                    {Object.keys(reviewingRequest.verification_diff).length === 0 && <div>No fields changed.</div>}
                    {Object.entries(reviewingRequest.verification_diff).map(([k, v]) => (
                      <div key={k}>· <b>{k}</b>: {JSON.stringify(v.before)} → {JSON.stringify(v.after)}</div>
                    ))}
                  </div>
                )}
                {reviewingRequest.decision_note && (
                  <div className="text-xs text-stone-600">Note: {reviewingRequest.decision_note}</div>
                )}
                {reviewingRequest.status === 'pending' && (
                  <>
                    <div className="border-t border-stone-200 pt-3">
                      <Label className="text-xs text-stone-500 font-semibold">
                        Apply changes to the record
                      </Label>
                      <div className="text-xs text-stone-500 mb-2">
                        Set the corrected values below and click Approve. The record updates directly (no TimeTec sync needed) and is signed off automatically.
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">In time (HH:MM:SS)</Label>
                          <Input value={decisionEdits.in_time}
                            onChange={e => setDecisionEdits({ ...decisionEdits, in_time: e.target.value })}
                            placeholder="07:00:00"
                            data-testid="decision-in-time" />
                        </div>
                        <div>
                          <Label className="text-xs">Out time (HH:MM:SS)</Label>
                          <Input value={decisionEdits.out_time}
                            onChange={e => setDecisionEdits({ ...decisionEdits, out_time: e.target.value })}
                            placeholder="17:00:00"
                            data-testid="decision-out-time" />
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs">
                            Breaks <span className="text-stone-400">({(decisionEdits.breaks || []).length})</span>
                          </Label>
                          <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                            data-testid="decision-add-break"
                            onClick={() => setDecisionEdits(f => ({ ...f, breaks: [...(f.breaks || []), { out: '', in: '' }] }))}>
                            + Add break
                          </Button>
                        </div>
                        {(decisionEdits.breaks || []).length === 0 ? (
                          <div className="text-xs text-stone-400 italic">No breaks.</div>
                        ) : (
                          <div className="space-y-2">
                            {decisionEdits.breaks.map((b, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <span className="text-stone-400 text-xs w-6">{i + 1}.</span>
                                <Input value={b.out || ''}
                                  onChange={e => setDecisionEdits(f => {
                                    const nb = [...(f.breaks || [])]; nb[i] = { ...nb[i], out: e.target.value };
                                    return { ...f, breaks: nb };
                                  })}
                                  placeholder="12:00:00" className="h-8 text-sm" />
                                <span className="text-stone-400">→</span>
                                <Input value={b.in || ''}
                                  onChange={e => setDecisionEdits(f => {
                                    const nb = [...(f.breaks || [])]; nb[i] = { ...nb[i], in: e.target.value };
                                    return { ...f, breaks: nb };
                                  })}
                                  placeholder="12:30:00" className="h-8 text-sm" />
                                <Button type="button" variant="ghost" size="sm" className="text-rose-600 h-8"
                                  onClick={() => setDecisionEdits(f => ({ ...f, breaks: (f.breaks || []).filter((_, j) => j !== i) }))}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="mt-3">
                        <Label className="text-xs">Remark (optional)</Label>
                        <Input value={decisionEdits.remark}
                          onChange={e => setDecisionEdits({ ...decisionEdits, remark: e.target.value })}
                          placeholder="e.g. Forgot to clock out"
                          data-testid="decision-remark" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">Note to submitter (optional)</Label>
                      <Input value={decisionNote} onChange={e => setDecisionNote(e.target.value)}
                        placeholder="e.g. Applied requested changes"
                        data-testid="decision-note-input" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" className="text-rose-600 border-rose-300"
                        onClick={() => decideRequest(reviewingRequest, 'decline')}
                        data-testid="decline-request-button">
                        <XCircle className="w-4 h-4 mr-1" /> Decline
                      </Button>
                      <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => decideRequest(reviewingRequest, 'approve')}
                        data-testid="approve-request-button">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Direct edit attendance dialog (Admin only) */}
      <Dialog open={!!editingRecord} onOpenChange={(o) => { if (!o) setEditingRecord(null); }}>
        <DialogContent className="max-w-2xl" data-testid="edit-attendance-dialog">
          {editingRecord && (
            <>
              <DialogHeader>
                <DialogTitle>Edit attendance record</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="bg-stone-50 rounded p-3 text-xs grid grid-cols-2 gap-2">
                  <div><b>Staff:</b> {editingRecord.name}</div>
                  <div><b>Date:</b> {editingRecord.date}</div>
                  <div><b>Source:</b> {editingRecord.source || 'timetec'}</div>
                  <div><b>Branch:</b> {editingRecord.branch
                    ? <span className="text-stone-800">{editingRecord.branch}</span>
                    : <span className="text-stone-400 italic">unresolved — map devices in Settings → Integrations → TimeTec Clocking Devices, then run “Resolve attendance branches”</span>
                  }</div>
                  <div><b>Day:</b> {editingRecord.is_public_holiday ? 'Public holiday' : editingRecord.is_sunday ? 'Sunday' : 'Weekday'}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">In time (HH:MM:SS)</Label>
                    <Input value={editForm.in_time} onChange={e => setEditForm({ ...editForm, in_time: e.target.value })}
                      placeholder="07:00:00" data-testid="edit-in-time" />
                  </div>
                  <div>
                    <Label className="text-xs">Out time (HH:MM:SS)</Label>
                    <Input value={editForm.out_time} onChange={e => setEditForm({ ...editForm, out_time: e.target.value })}
                      placeholder="17:00:00" data-testid="edit-out-time" />
                  </div>
                  <div className="col-span-2 border-t border-stone-200 pt-3 mt-1">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs">
                        Breaks <span className="text-stone-400">({(editForm.breaks || []).length})</span>
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        data-testid="edit-add-break"
                        onClick={() => setEditForm(f => ({
                          ...f,
                          breaks: [...(f.breaks || []), { out: '', in: '' }],
                        }))}
                      >
                        + Add break
                      </Button>
                    </div>
                    {(editForm.breaks || []).length === 0 ? (
                      <div className="text-xs text-stone-400 italic">
                        No breaks. Click "+ Add break" to record one.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(editForm.breaks || []).map((b, i) => (
                          <div key={i} className="flex items-center gap-2"
                            data-testid={`edit-break-row-${i}`}>
                            <span className="text-stone-400 text-xs w-6">{i + 1}.</span>
                            <div className="flex-1">
                              <Label className="text-[10px] text-rose-600">Break out</Label>
                              <Input
                                value={b.out || ''}
                                onChange={e => setEditForm(f => {
                                  const next = [...(f.breaks || [])];
                                  next[i] = { ...next[i], out: e.target.value };
                                  return { ...f, breaks: next };
                                })}
                                placeholder="12:00:00"
                                className="h-8 text-sm"
                                data-testid={`edit-break-${i}-out`}
                              />
                            </div>
                            <div className="flex-1">
                              <Label className="text-[10px] text-emerald-700">Break in</Label>
                              <Input
                                value={b.in || ''}
                                onChange={e => setEditForm(f => {
                                  const next = [...(f.breaks || [])];
                                  next[i] = { ...next[i], in: e.target.value };
                                  return { ...f, breaks: next };
                                })}
                                placeholder="12:30:00"
                                className="h-8 text-sm"
                                data-testid={`edit-break-${i}-in`}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-rose-600 h-8 mt-4"
                              onClick={() => setEditForm(f => ({
                                ...f,
                                breaks: (f.breaks || []).filter((_, j) => j !== i),
                              }))}
                              data-testid={`edit-break-${i}-remove`}
                              title="Remove this break"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Work hours <span className="text-stone-400">(auto-recomputes buckets)</span></Label>
                    <Input type="number" step="0.25" value={editForm.work_hours}
                      onChange={e => setEditForm({ ...editForm, work_hours: e.target.value })}
                      data-testid="edit-work-hours" />
                  </div>
                  <div>
                    <Label className="text-xs">Leave hours</Label>
                    <Input type="number" step="0.25" value={editForm.leave_hours}
                      onChange={e => setEditForm({ ...editForm, leave_hours: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Ordinary hours <span className="text-stone-400">(override)</span></Label>
                    <Input type="number" step="0.25" value={editForm.ordinary_hours}
                      onChange={e => setEditForm({ ...editForm, ordinary_hours: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Weekly OT</Label>
                    <Input type="number" step="0.25" value={editForm.weekly_ot_hours}
                      onChange={e => setEditForm({ ...editForm, weekly_ot_hours: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Sunday hours</Label>
                    <Input type="number" step="0.25" value={editForm.sunday_hours}
                      onChange={e => setEditForm({ ...editForm, sunday_hours: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Public holiday hours</Label>
                    <Input type="number" step="0.25" value={editForm.public_holiday_hours}
                      onChange={e => setEditForm({ ...editForm, public_holiday_hours: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Leave type</Label>
                    <Input value={editForm.leave_type}
                      onChange={e => setEditForm({ ...editForm, leave_type: e.target.value })}
                      placeholder="Annual, Sick…" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Branch</Label>
                  <Select
                    value={editForm.branch || '__none__'}
                    onValueChange={v => setEditForm({ ...editForm, branch: v === '__none__' ? '' : v })}>
                    <SelectTrigger data-testid="edit-attendance-branch"><SelectValue placeholder="— no branch —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— no branch —</SelectItem>
                      {branches.map(b => (
                        <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500 mt-1">
                    Overrides any device-resolved branch (e.g. TimeTec). Only change this if the clocking device was mapped to the wrong branch or the staffer was elsewhere that day.
                  </p>
                </div>
                <div>
                  <Label className="text-xs">Note / remark</Label>
                  <Input value={editForm.remark}
                    onChange={e => setEditForm({ ...editForm, remark: e.target.value })} />
                </div>
                <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                  Saving will mark this record as admin-overridden — future biometric punches won&apos;t overwrite it. Use the date&apos;s edit history if you need to revert.
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setEditingRecord(null)}>Cancel</Button>
                  <Button onClick={submitEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="save-attendance-edit">
                    Save changes
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Attendance dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-md" data-testid="export-attendance-dialog">
          <DialogHeader>
            <DialogTitle>Export Attendance</DialogTitle>
            <p className="text-sm text-stone-500">Choose export type and date range</p>
          </DialogHeader>
          <div className="space-y-5">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 flex items-start gap-2"
              data-testid="signed-off-only-banner">
              <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Exports include <b>only records signed off by both the employee and manager</b>
                {' '}(or admin-overridden). Unsigned rows are excluded so payroll and gratuity
                figures always reconcile with what was approved.
              </span>
            </div>
            <div>
              <div className="font-semibold text-stone-800 mb-2">Report Type</div>
              <div className="space-y-2">
                {[
                  { key: 'detailed', label: 'Detailed Report',
                    desc: 'Per-record breakdown — clock in/out, breaks, and daily hours. If the range spans a single week, filename uses the ISO week number.' },
                  { key: 'summary', label: 'Summary Report',
                    desc: 'Per-employee totals with Sunday hours, public-holiday hours, weekly overtime, and Gratuities pulled live from Cash Recon.' },
                  { key: 'simplepay', label: 'SimplePay File (.csv)',
                    desc: 'Same totals as the Summary for payroll import — single sheet CSV, employee rows only, without employee name, days worked or night shift count.' },
                ].map(opt => (
                  <label key={opt.key}
                    className={`block rounded-xl border p-3 cursor-pointer transition-colors ${
                      exportReportType === opt.key
                        ? 'border-amber-500 bg-amber-50/50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid={`export-type-${opt.key}-option`}>
                    <div className="flex items-start gap-2">
                      <input
                        type="radio"
                        name="export-type"
                        value={opt.key}
                        checked={exportReportType === opt.key}
                        onChange={() => setExportReportType(opt.key)}
                        className="mt-1 accent-amber-500"
                        data-testid={`export-type-${opt.key}-input`}
                      />
                      <div>
                        <div className="font-medium text-stone-800">{opt.label}</div>
                        <div className="text-xs text-stone-500 mt-0.5">{opt.desc}</div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="font-semibold text-stone-800 mb-2">Date Range</div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer"
                  data-testid="export-range-current-week-option">
                  <input
                    type="radio"
                    name="export-range"
                    checked={exportRangeMode === 'current_week'}
                    onChange={() => setExportRangeMode('current_week')}
                    className="accent-amber-500"
                    data-testid="export-range-current-week-input"
                  />
                  <span className="text-sm">
                    Current Week ({currentWeekRange().from} to {currentWeekRange().to})
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer"
                  data-testid="export-range-custom-option">
                  <input
                    type="radio"
                    name="export-range"
                    checked={exportRangeMode === 'custom'}
                    onChange={() => setExportRangeMode('custom')}
                    className="accent-amber-500"
                    data-testid="export-range-custom-input"
                  />
                  <span className="text-sm">Custom Date Range</span>
                </label>
                {exportRangeMode === 'custom' && (
                  <div className="flex gap-2 ml-6 mt-1">
                    <div>
                      <Label className="text-xs">From</Label>
                      <Input type="date" value={exportCustomFrom}
                        onChange={(e) => setExportCustomFrom(e.target.value)}
                        className="h-9"
                        data-testid="export-custom-from" />
                    </div>
                    <div>
                      <Label className="text-xs">To</Label>
                      <Input type="date" value={exportCustomTo}
                        onChange={(e) => setExportCustomTo(e.target.value)}
                        className="h-9"
                        data-testid="export-custom-to" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {exportReportType !== 'detailed' ? (
              <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50 text-xs text-emerald-900" data-testid="export-summary-signed-note">
                Summary uses <b>signed-off hours only</b> and the exact same calculation as the staff payroll-period screen, so the numbers always match.
              </div>
            ) : (
            <div>
              <div className="font-semibold text-stone-800 mb-2">Hours to Include</div>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer p-3 border border-stone-200 rounded-lg hover:bg-stone-50"
                  data-testid="export-scope-signed-option">
                  <input
                    type="radio"
                    name="export-scope"
                    checked={exportHoursScope === 'signed_only'}
                    onChange={() => setExportHoursScope('signed_only')}
                    className="mt-1 accent-emerald-600"
                    data-testid="export-scope-signed-input"
                  />
                  <div>
                    <div className="font-medium text-stone-800">Signed-off hours only <span className="text-xs text-emerald-700">· recommended</span></div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      Includes only records where both the employee and their manager have signed off (or admin-override). Best for payroll.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-3 border border-stone-200 rounded-lg hover:bg-stone-50"
                  data-testid="export-scope-all-option">
                  <input
                    type="radio"
                    name="export-scope"
                    checked={exportHoursScope === 'all_hours'}
                    onChange={() => setExportHoursScope('all_hours')}
                    className="mt-1 accent-amber-600"
                    data-testid="export-scope-all-input"
                  />
                  <div>
                    <div className="font-medium text-stone-800">All hours <span className="text-xs text-amber-700">· includes unsigned</span></div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      Every record in the date range, signed or not. Useful for operational reviews and reconciliations.
                    </div>
                  </div>
                </label>
              </div>
            </div>
            )}

            <div>
              <div className="font-semibold text-stone-800 mb-2">Optional Filters</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Employee</Label>
                  <Select value={exportEmployeeFilter} onValueChange={setExportEmployeeFilter}>
                    <SelectTrigger data-testid="export-employee-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All employees</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Branch</Label>
                  <Select value={exportBranchFilter} onValueChange={setExportBranchFilter}>
                    <SelectTrigger data-testid="export-branch-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All branches</SelectItem>
                      {branches.map(b => (
                        <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setExportDialogOpen(false)}
                disabled={downloadingSummary}
                data-testid="export-cancel-button">
                Cancel
              </Button>
              <Button onClick={runExport} disabled={downloadingSummary}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="export-confirm-button">
                <Download className={`w-4 h-4 mr-2 ${downloadingSummary ? 'animate-pulse' : ''}`} />
                {downloadingSummary ? 'Building…' : 'Export'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Manual Attendance Record dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-2xl" data-testid="manual-attendance-dialog">
          <DialogHeader>
            <DialogTitle>Add Manual Attendance Record</DialogTitle>
            <p className="text-sm text-stone-500">
              Master admin only. Creates a fully signed-off record — both employee and manager sign-off are stamped as you, and admin-override is set so biometric re-aggregation can&apos;t overwrite it. Breaks are deducted from the total.
            </p>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Staff *</Label>
                <Select value={manualForm.user_id}
                  onValueChange={v => {
                    const u = users.find(x => x.id === v);
                    setManualForm(f => ({
                      ...f,
                      user_id: v,
                      branch: u?.branch || f.branch,
                    }));
                  }}>
                  <SelectTrigger data-testid="manual-user-select"><SelectValue placeholder="Select staff…" /></SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.is_active !== false).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={manualForm.date}
                  onChange={e => setManualForm({ ...manualForm, date: e.target.value })}
                  data-testid="manual-date" />
              </div>
              <div>
                <Label className="text-xs">Branch</Label>
                <Select value={manualForm.branch || 'auto'}
                  onValueChange={v => setManualForm({ ...manualForm, branch: v === 'auto' ? '' : v })}>
                  <SelectTrigger data-testid="manual-branch-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Use staff&apos;s home branch</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Leave hours (optional)</Label>
                <Input type="number" step="0.25" min="0" value={manualForm.leave_hours}
                  onChange={e => setManualForm({ ...manualForm, leave_hours: e.target.value })}
                  placeholder="0"
                  data-testid="manual-leave-hours" />
              </div>
            </div>

            <div className="border-t border-stone-200 pt-3">
              <div className="text-xs font-semibold text-stone-700 mb-2">Times (HH:MM, 24-hour)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Clock In *</Label>
                  <Input type="time" value={manualForm.in_time}
                    onChange={e => setManualForm({ ...manualForm, in_time: e.target.value })}
                    data-testid="manual-in-time" />
                </div>
                <div>
                  <Label className="text-xs">Clock Out *</Label>
                  <Input type="time" value={manualForm.out_time}
                    onChange={e => setManualForm({ ...manualForm, out_time: e.target.value })}
                    data-testid="manual-out-time" />
                </div>
                <div>
                  <Label className="text-xs">Break 1 Out</Label>
                  <Input type="time" value={manualForm.break1_out}
                    onChange={e => setManualForm({ ...manualForm, break1_out: e.target.value })}
                    data-testid="manual-break1-out" />
                </div>
                <div>
                  <Label className="text-xs">Break 1 In</Label>
                  <Input type="time" value={manualForm.break1_in}
                    onChange={e => setManualForm({ ...manualForm, break1_in: e.target.value })}
                    data-testid="manual-break1-in" />
                </div>
                <div>
                  <Label className="text-xs">Break 2 Out</Label>
                  <Input type="time" value={manualForm.break2_out}
                    onChange={e => setManualForm({ ...manualForm, break2_out: e.target.value })}
                    data-testid="manual-break2-out" />
                </div>
                <div>
                  <Label className="text-xs">Break 2 In</Label>
                  <Input type="time" value={manualForm.break2_in}
                    onChange={e => setManualForm({ ...manualForm, break2_in: e.target.value })}
                    data-testid="manual-break2-in" />
                </div>
              </div>
              <div className="text-xs text-stone-500 mt-2">
                Only <b>complete</b> break pairs (both out + in) are deducted. Incomplete rows are ignored.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Leave type</Label>
                <Input value={manualForm.leave_type}
                  onChange={e => setManualForm({ ...manualForm, leave_type: e.target.value })}
                  placeholder="Annual, Sick…" data-testid="manual-leave-type" />
              </div>
              <div>
                <Label className="text-xs">Note / remark</Label>
                <Input value={manualForm.remark}
                  onChange={e => setManualForm({ ...manualForm, remark: e.target.value })}
                  placeholder="e.g. Manual entry — device offline"
                  data-testid="manual-remark" />
              </div>
            </div>

            <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
              This record will be tagged <b>source=manual</b>, admin-overridden, and fully signed off — it will count in both attendance exports and gratuities apportionment.
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setManualOpen(false)}
                disabled={manualSaving}
                data-testid="manual-cancel-button">Cancel</Button>
              <Button onClick={submitManual}
                disabled={manualSaving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="manual-save-button">
                {manualSaving ? 'Saving…' : 'Create record'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminAttendanceTab;
